import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Avatar, Button, IconButton } from '../primitives/primitives.js';
import { ActionMenu, type MenuItem } from '../overlays/overlays.js';
import { BrandMark, Icon, type IconName } from '../icons/icons.js';
import styles from './shell.module.css';

/**
 * TESSMA ONE FROZEN application shell.
 *
 * Transcribed from the approved design. The behavioural change from the
 * prototype is accessibility: navigation rows are real buttons with
 * `aria-current`, `aria-expanded` and keyboard activation; the search input is
 * labelled; icon-only controls carry names. The Phase 1 audit records all of
 * these as designed but not implemented (R-08).
 *
 * Navigation here is presentational. Routing, entitlement filtering and
 * permission guards are Stage 1 shell work and Stage 2 entitlement work
 * respectively; this component takes what it renders as props.
 */

export interface ShellModule {
  readonly id: string;
  readonly label: string;
  /** A name from the frozen §16 icon vocabulary — see MODULE_ICONS. */
  readonly icon: IconName;
  readonly pages: ReadonlyArray<string>;
}

export interface AppShellProps {
  readonly modules: ReadonlyArray<ShellModule>;
  readonly activeModuleId: string;
  readonly activePage: string;
  readonly onNavigate: (moduleId: string, page: string) => void;
  readonly user: { readonly name: string; readonly role: string; readonly initials: string };
  readonly onAskAi?: () => void;
  /**
   * Renders the three header affordances that belong to later stages — global
   * search, Ask TESSMA AI and notifications — in the frozen v1.1 **disabled**
   * treatment (45% opacity of the resting token, owner decision 10).
   *
   * Additive and opt-in: the default is the frozen enabled rendering, so no
   * existing surface or baseline changes. It exists because the alternative
   * treatments are both worse — a control that looks live and does nothing
   * lies about the product, and hiding it changes the frozen layout.
   *
   * Disabled controls leave the tab order, which is the correct announcement
   * for something unavailable; each keeps a name that says so. The notification
   * dot is not rendered, because an unread count for a feature that does not
   * exist would be fabricated data rather than a design detail.
   */
  readonly inertAffordances?: boolean;
  /**
   * Makes global search live. Called with the query when the reader presses
   * Enter; ⌘K / Ctrl+K focuses the field.
   *
   * SAME RULE AS `onAskAi`: supplying a handler IS the implementation. Without
   * one the field stays in the frozen v1.1 disabled treatment, and the ⌘K
   * listener is not installed — a shortcut that focuses a disabled field would
   * be a second thing claiming to work.
   */
  readonly onSearch?: (query: string) => void;
  /**
   * The notifications entry point.
   *
   * THE COUNT IS DATA AND COMES FROM THE CALLER. It used to be the literal
   * string "Notifications, 3 unread" written into this component, announced on
   * every page of the product to every screen-reader user, for a feature with
   * no data source behind it. A number nobody can produce is fabricated data
   * whatever it is doing in a design.
   *
   * `unreadCount` omitted means the caller does not know: no dot, and a plain
   * "Notifications". Zero is a real answer and says so.
   */
  readonly notifications?: {
    readonly unreadCount?: number | undefined;
    readonly onOpen?: (() => void) | undefined;
  };
  /**
   * The account menu behind the header avatar. Empty or absent renders no
   * avatar at all: a control whose menu has nothing in it is worse than no
   * control, and the sidebar already says who is signed in.
   */
  readonly accountItems?: ReadonlyArray<MenuItem>;
  /**
   * One line under the `<h1>` saying what this page is for.
   *
   * IN THE HEADER, WITH THE TITLE, because that is where the approved design
   * puts it — and because the alternative was worse. Before P1 the description
   * lived in a card at the top of the page body, under a second copy of the
   * page's name, so the reader met the title twice and the explanation after a
   * card border. It comes from the same route table the breadcrumb and the
   * title come from, so the three cannot disagree about what page this is.
   *
   * Absent renders nothing and changes no layout, which is what keeps every
   * surface that has no description exactly as it was.
   */
  readonly pageDescription?: string | undefined;
  /**
   * The `<h1>`, when it is not simply the page's name.
   *
   * The breadcrumb and the sidebar say where you are — "Dashboard › Overview" —
   * and the frozen reference's heading says what the page is: "Dashboard
   * Overview". Both come from the route table, so they still cannot disagree;
   * this only lets the heading be the longer of the two.
   */
  readonly pageTitle?: string | undefined;
  /** Opens help. Absent leaves the control out entirely rather than dead. */
  readonly onHelp?: (() => void) | undefined;
  readonly children: ReactNode;
}

export function AppShell({
  modules,
  activeModuleId,
  activePage,
  onNavigate,
  user,
  onAskAi,
  inertAffordances = false,
  onSearch,
  notifications,
  accountItems,
  pageDescription,
  pageTitle,
  onHelp,
  children,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeModule = modules.find((module) => module.id === activeModuleId);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const searchLive = onSearch !== undefined;

  /*
   * ⌘K FOCUSES SEARCH, BECAUSE THE BADGE SAYS IT DOES.
   *
   * The frozen header has always drawn a ⌘K badge beside the field. Until the
   * field had a handler that badge was part of a control that plainly did not
   * work; now that it does, the shortcut is the difference between a label and
   * a promise. Installed only when search is live, and removed with it.
   */
  useEffect(() => {
    if (!searchLive) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [searchLive]);

  const unread = notifications?.unreadCount;
  const notificationsLive = notifications?.onOpen !== undefined;
  const notificationsDisabled = inertAffordances && !notificationsLive;
  const notificationLabel = notificationsDisabled
    ? 'Notifications — not available yet'
    : unread === undefined
      ? 'Notifications'
      : unread === 0
        ? 'Notifications, none unread'
        : `Notifications, ${String(unread)} unread`;

  return (
    <div className={styles.shell}>
      {mobileOpen && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={[styles.sidebar, mobileOpen ? styles.sidebarOpen : ''].filter(Boolean).join(' ')}
        aria-label="Primary"
      >
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <BrandMark size={17} />
          </span>
          <span className={styles.brandWord}>
            <span className={styles.brandPrimary}>TESSMA</span>
            <span className={styles.brandSecondary}>ONE</span>
          </span>
        </div>
        <div className={styles.brandDivider} />

        <nav className={styles.nav} aria-label="Modules">
          <ul className={styles.navList}>
            {modules.map((module) => {
              const isActive = module.id === activeModuleId;
              return (
                <li key={module.id}>
                  <button
                    type="button"
                    className={[styles.navRow, isActive ? styles.navRowActive : '']
                      .filter(Boolean)
                      .join(' ')}
                    aria-current={isActive ? 'page' : undefined}
                    aria-expanded={isActive}
                    onClick={() => {
                      const first = module.pages[0];
                      if (first !== undefined) onNavigate(module.id, first);
                    }}
                  >
                    <span className={styles.navIcon}>
                      <Icon name={module.icon} size={19} />
                    </span>
                    <span className={styles.navLabel} title={module.label}>
                      {module.label}
                    </span>
                    <span
                      className={[styles.navCaret, isActive ? styles.navCaretOpen : '']
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden="true"
                    >
                      <Icon name="caret-down" size={12} />
                    </span>
                  </button>

                  {/* Only the active module exposes its child navigation (§7). */}
                  {isActive && (
                    <ul className={styles.subList}>
                      {module.pages.map((page) => {
                        const isCurrent = page === activePage;
                        return (
                          <li key={page}>
                            <button
                              type="button"
                              className={[styles.subRow, isCurrent ? styles.subRowCurrent : '']
                                .filter(Boolean)
                                .join(' ')}
                              aria-current={isCurrent ? 'page' : undefined}
                              onClick={() => onNavigate(module.id, page)}
                            >
                              <span className={styles.subDot} aria-hidden="true" />
                              <span className={styles.subLabel} title={page}>
                                {page}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={styles.sidebarFooter}>
          <Avatar initials={user.initials} size={32} tone="navy" name={user.name} />
          <span className={styles.footerText}>
            <span className={styles.footerName}>{user.name}</span>
            <span className={styles.footerRole}>{user.role}</span>
          </span>
          <button type="button" className={styles.footerAction} aria-label="Settings">
            <Icon name="gear-six" size={17} />
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <span className={styles.hamburger}>
            <IconButton label="Open navigation" size="lg" onClick={() => setMobileOpen(true)}>
              <Icon name="list" size={17} />
            </IconButton>
          </span>

          <div className={styles.headerTitles}>
            <div className={styles.breadcrumb}>
              <span>{activeModule?.label ?? ''}</span>
              <span className={styles.breadcrumbCaret} aria-hidden="true">
                <Icon name="caret-right" size={11} />
              </span>
              <span className={styles.breadcrumbCurrent}>{activePage}</span>
            </div>
            <h1 className={styles.pageTitle}>{pageTitle ?? activePage}</h1>
            {pageDescription !== undefined && (
              <p className={styles.pageDescription}>{pageDescription}</p>
            )}
          </div>

          <div
            role="search"
            className={[styles.search, inertAffordances && !searchLive ? styles.searchInert : '']
              .filter(Boolean)
              .join(' ')}
          >
            <span className={styles.searchIcon}>
              <Icon name="magnifying-glass" size={16} />
            </span>
            <input
              ref={searchRef}
              className={styles.searchInput}
              type="search"
              placeholder="Search TESSMA ONE"
              aria-label={
                inertAffordances && !searchLive
                  ? 'Search TESSMA ONE — not available yet'
                  : 'Search TESSMA ONE'
              }
              disabled={inertAffordances && !searchLive}
              onKeyDown={
                onSearch === undefined
                  ? undefined
                  : (event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      const query = event.currentTarget.value.trim();
                      if (query.length > 0) onSearch(query);
                    }
              }
            />
            <span className={styles.searchBadge} aria-hidden="true">
              ⌘K
            </span>
          </div>

          <div className={styles.headerActions}>
            <Button
              variant="ai"
              headerScale
              icon={<Icon name="sparkle" size={15} />}
              onClick={onAskAi}
              /*
               * INERT UNTIL A HANDLER EXISTS, not until a stage says so.
               *
               * `inertAffordances` has meant "these three have no
               * implementation yet" since Stage 1. Stage 7 implements exactly
               * one of the three, so the flag alone can no longer answer the
               * question — and splitting it into three flags would put the
               * decision in the caller, where it can be got wrong.
               *
               * SUPPLYING A HANDLER *IS* THE IMPLEMENTATION, so that is what
               * the button reads. Search and notifications pass none and stay
               * in the frozen disabled treatment (OD-4 §2); nothing visual
               * changes for any of the three.
               */
              disabled={inertAffordances && onAskAi === undefined}
              aria-label={
                inertAffordances && onAskAi === undefined
                  ? 'Ask TESSMA AI — not available yet'
                  : undefined
              }
            >
              Ask TESSMA AI
            </Button>
            {onHelp !== undefined && (
              <IconButton label="Help and support" size="lg" onClick={onHelp}>
                <Icon name="info" size={17} />
              </IconButton>
            )}
            <span className={styles.notification}>
              <IconButton
                label={notificationLabel}
                size="lg"
                disabled={notificationsDisabled}
                {...(notifications?.onOpen === undefined ? {} : { onClick: notifications.onOpen })}
              >
                <Icon name="bell" size={17} />
              </IconButton>
              {/* The dot marks unread mail. No count, no dot — it would be
                  marking something nobody counted. */}
              {unread !== undefined && unread > 0 && (
                <span className={styles.notificationDot} aria-hidden="true" />
              )}
            </span>
            {accountItems !== undefined && accountItems.length > 0 && (
              <ActionMenu
                label={`Account — ${user.name}, ${user.role}`}
                items={accountItems}
                trigger={
                  <button
                    type="button"
                    className={styles.account}
                    aria-haspopup="menu"
                    aria-label={`Account — ${user.name}, ${user.role}`}
                  >
                    <Avatar initials={user.initials} size={32} tone="navy" name={user.name} />
                  </button>
                }
              />
            )}
          </div>
        </header>

        <main className={styles.page}>{children}</main>
      </div>
    </div>
  );
}
